import {
  FC,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InputAutoComplete from "./InputAutoComplete";
import Icons from "../assets/Icons";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

interface DropdownProps {
  key: string,
  value: string,
}
interface DropdownValuesProps {
  key: string,
  value: Array<DropdownProps>,
  isMulti?: boolean,
}

interface SelectedFilterProps {
  filterType: string,
  condition: string,
  selectedValues: [],
}

interface FilterProps {
  totalLength?: number,
  control: any,
  filterField: Array<DropdownProps>,
  filterConditions: Array<DropdownProps>,
  filterIndex: number,
  filterValueDropdown: Array<DropdownValuesProps>,
  handleAddFilter: () => void,
  handleDeleteFilter: (index: number) => void,
  selectedAdvancedFilters: Array<SelectedFilterProps>
}

interface AdvancedFilterProps {
  main_title: string,
  selectedAdvancedFilters: Array<SelectedFilterProps>,
  control: any,
  filterField: Array<DropdownProps>,
  filterConditions: Array<DropdownProps>,
  filterValueDropdown: Array<DropdownValuesProps>,
  handleAddFilter: () => void,
  handleDeleteFilter: (index: number) => void,
}

const Filter: FC<FilterProps> = ({
  totalLength = 0,
  control,
  filterField,
  filterConditions,
  filterIndex,
  filterValueDropdown,
  handleDeleteFilter,
  handleAddFilter,
  selectedAdvancedFilters
}) => {
  const selectedType = useWatch({
    control,
    name: `type_${filterIndex}`
  });

  const { valueOptions, isMulti } = useMemo(() => {
    if (selectedType !== undefined) {
      const matched = filterValueDropdown?.find(
        item => item?.key === selectedType
      );
      return {
        valueOptions: matched?.value || [],
        isMulti: matched?.isMulti ?? true,
      };
    }
    return { valueOptions: [], isMulti: true };
  }, [selectedType, filterValueDropdown]);

  const _filterField = filterField.filter(item =>
    item.key === selectedType ||
    !selectedAdvancedFilters.some(
      selected => selected.filterType === item.key
    )
  );

  return (
    <Box sx={{ width: '100%', marginTop: 1, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', gap: 1, marginBottom: 1, alignItems: 'center' }}>
        <Box sx={{ width: '90%' }}>
          <InputAutoComplete
            name={`type_${filterIndex}`}
            control={control}
            isMulti={false}
            options={_filterField}
          />
        </Box>
        <Box sx={{ width: '10%' }}> {totalLength > 1 && <Icons.DeleteIcon onClick={() => handleDeleteFilter(filterIndex)} />} </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ width: '40%' }}>
          <InputAutoComplete
            name={`condition_${filterIndex}`}
            control={control}
            isMulti={false}
            options={filterConditions}
          />
        </Box>
        <Box sx={{ width: '50%' }}>
          <InputAutoComplete
            name={`options_${filterIndex}`}
            control={control}
            isMulti={isMulti}
            options={valueOptions}
          />
        </Box>
        <Box sx={{ width: '10%' }}> {((totalLength - 1) === filterIndex && totalLength !== 7) && <Icons.RoundedPlus onClick={() => handleAddFilter()} />} </Box>
      </Box>

      {((totalLength - 1) !== filterIndex) && <Divider sx={{ mb: 1, height: 2, borderBottomWidth: 1, borderColor: "#79747E" }} />}
    </Box>
  )
}

const AdvancedFilter: FC<AdvancedFilterProps> = ({
  main_title,
  control,
  filterField,
  filterConditions,
  filterValueDropdown,
  selectedAdvancedFilters,
  handleAddFilter,
  handleDeleteFilter
}) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <Box sx={{
      border: 1,
      borderRadius: 1,
      borderColor: '#1D1B20',
      mb: 1,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'space-between', gap: 1, p: 1 }}>
        <Typography sx={{ fontWeight: 700, fontStyle: 'bold', fontSize: '16px', lineHeight: '20px' }}>{main_title}</Typography>

        <IconButton onClick={() => setOpen(!open)}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box>
          <Box
            sx={{
              width: "100%",
              borderBottom: "1px solid #1D1B20",
              mb: 2,
            }}
          />
          <Box sx={{
            display: "flex", flexWrap: "wrap", m: 2, borderRadius: 4,
            p: 2, backgroundColor: '#EBEBEB'
          }}>
            {selectedAdvancedFilters.map((filter, index) => {
              return (
                <Filter
                  key={`${filter.filterType}_${index}`}
                  totalLength={selectedAdvancedFilters.length}
                  control={control}
                  filterField={filterField}
                  filterConditions={filterConditions}
                  filterIndex={index}
                  filterValueDropdown={filterValueDropdown}
                  handleAddFilter={handleAddFilter}
                  handleDeleteFilter={handleDeleteFilter}
                  selectedAdvancedFilters={selectedAdvancedFilters}
                />
              )
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'space-between', gap: 1, p: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}  >
              <Box sx={{ padding: 1 }}> {<Icons.InfoBlue />} </Box>
              <Typography sx={{ fontSize: '12px', color: 'black', marginTop: 1 }}>
                {t('Max_selection_7')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse >
    </Box >
  );
};

export default AdvancedFilter;
